<template>
  <div class="asc-mx-rec-upcoming-flights--wrapper">
    <template v-if="isExpanded">
      <div class="upcoming-flights-grid-wrapper">
        <i v-if="flightAwareLastUpdatedDate" class="lastUpdated">
          Data last updated on: {{ getLastUpdatedDate }}
        </i>
        <asc-ag-grid-filter
          class="grid"
          :grid-options="agGridOptions"
          :column-defs="getGridColumnDefs"
          :row-data="getGridValues"
        />
      </div>
    </template>
    <template v-else>
      <div class="condensed-grid">
        <div class="upcoming-flights">
          <i v-if="flightAwareLastUpdatedDate" class="lastUpdated">
            Data last updated on: {{ getLastUpdatedDate }}
          </i>
          <template v-if="getGridValues.length">
            <div
              v-for="(row, index) in getGridValues"
              :key="index"
              class="upcoming-flights--row"
            >
              <div
                v-if="row.isGroundTime && row.isGTmorethenfourhours"
                class="rowDetail groundTime"
              >
                <i
                  aria-hidden="true"
                  class="v-icon notranslate mdi mdi-square"
                />
                <div>
                  <div>{{ getFormatedDuration(row.duration) }} Ground Time</div>
                  <div class="time">
                    Starting: {{ getFormatedUTCDateTime(row.time) }}
                  </div>
                </div>
              </div>
              <div
                v-if="!row.isGroundTime && row.hideGroundTime"
                class="rowDetail departing"
              >
                <div>{{ row.flight }}</div>
                <div class="time">
                  Departing: {{ getFormatedUTCDateTime(row.time) }}
                </div>
              </div>
            </div>
          </template>
          <p v-else>There are no scheduled flights for {{ tailName }}</p>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { getFilterOptions } from '@/components/agGrid/AgGridOptions'

import { AscGridPropsMixin, AscGridMethodsMixin } from '@/components/mixins'

import { AscAgGridFilter } from '@/components/agGrid'

import { getNewFormatUTCDateTime } from '@/utilities/dates'
import moment from 'moment'

export default {
  name: 'AscMxRecUpcomingFlightsWrapper',

  components: {
    AscAgGridFilter,
  },

  mixins: [AscGridPropsMixin, AscGridMethodsMixin],

  props: {
    isExpanded: {
      type: Boolean,
      default: false,
    },
    tailName: {
      type: String,
      default: '',
    },
    flightAwareLastUpdatedDate: {
      type: String,
      default: null,
    },
    futureFlightSchedules: {
      type: Array,
      required: true,
      default: () => [],
    },
  },

  data() {
    return {
      agGridOptions: getFilterOptions(this, {
        defaultColDef: {
          floatingFilter: false,
          tooltipComponent: 'AscTooltipCellRenderer',
        },
        suppressCellFocus: false,
        noRowsOverlayComponent: this.noOverlayComponent,
        onGridReady: this.handleGridReady,
        onGridSizeChanged: this.handleGridSizeChanged,
      }),
    }
  },

  computed: {
    getLastUpdatedDate() {
      return this.flightAwareLastUpdatedDate
        ? getNewFormatUTCDateTime(this.flightAwareLastUpdatedDate, true, ' | ')
        : ''
    },

    getGridValues() {
      const updatedRowData = []
      this.futureFlightSchedules.map(row => {
        updatedRowData.push(
          {
            isGroundTime: false,
            flight: `Flight ${row.flightNumber} from ${row.originAirportCode} to ${row.destinationAirportCode}`,
            duration: moment.duration(row.estimatedFlightDuration, 'seconds'),
            time: row.estimatedDepartureTime,
            hideGroundTime: true,
          },
          {
            isGroundTime: true,
            flight: 'Ground Time',
            duration: moment.duration(row.estimatedGroundTime, 'seconds'),
            time: row.estimatedArrivalTime,
            isGTmorethenfourhours:
              moment.duration(row.estimatedGroundTime, 'seconds')._data.hours >=
                4 &&
              moment.duration(row.estimatedGroundTime, 'seconds')._data
                .minutes >= 0,
            hideGroundTime: false,
          },
        )
      })

      return updatedRowData
    },

    getGridColumnDefs() {
      return [
        {
          headerName: 'Flight',
          field: 'flight',
          width: 320,
          suppressMovable: true,
          resizable: false,
          sortable: false,
          suppressSizeToFit: true,
          sortingOrder: ['asc', 'desc'],
          cellRenderer: params => {
            if (params.data?.isGroundTime) {
              return `<i aria-hidden="true" class="v-icon notranslate mdi mdi-square mr-1"></i> ${params.data?.flight}`
            } else {
              return `${params.data?.flight}`
            }
          },
        },
        {
          headerName: 'Duration (HRS)',
          field: 'duration',
          suppressMovable: true,
          suppressSizeToFit: false,
          maxWidth: 203,
          sortable: false,
          sortingOrder: ['asc', 'desc'],
          valueGetter: params => {
            if (params.data?.duration) {
              return `${this.getFormatedDuration(
                params.data?.duration,
                'hours,',
                'minutes',
              )}`
            }
            return ''
          },
        },
        {
          headerName: 'Departure / Start time (UTC)',
          flex: 1,
          field: 'time',
          sort: 'asc',
          suppressMovable: true,
          suppressSizeToFit: false,
          valueGetter: params => {
            if (params.data?.time) {
              return `${getNewFormatUTCDateTime(params.data?.time)}`
            }
            return ''
          },
        },
      ]
    },
  },

  methods: {
    getFormatedUTCDateTime(dateTime) {
      return getNewFormatUTCDateTime(dateTime)
    },

    getFormatedDuration(duration, hours = 'hrs.', minutes = 'min.') {
      return `${duration._data.hours} ${hours} ${duration._data.minutes} ${minutes}`
    },
  },
}
</script>

<style lang="scss">
.asc-mx-rec-upcoming-flights--wrapper {
  .v-icon.mdi-square {
    margin-right: 10px;
    color: $accent-600;
    font-size: 14px;
    line-height: 18px;
  }

  .condensed-grid {
    .upcoming-flights {
      &--row {
        margin-bottom: 10px;

        .rowDetail {
          .time {
            margin-top: 4px;
            color: $gray-600;
          }

          &.departing {
            margin-bottom: 10px;
          }
          &.groundTime {
            background: $gray-0;
            padding: 12px 10px;
            display: flex;
            align-items: start;

            .v-icon.mdi-square {
              margin-top: 1px;
            }
          }
        }
      }
    }
  }

  .lastUpdated {
    color: $gray-700;
    margin-bottom: 16px;
    display: block;
  }
}
</style>
